import Sidebar from "@components/sidebar/Sidebar";
import BuilderTopbar from "@components/topbar/BuilderTopbar";
import BuilderMain from "@components/main/BuilderMain";
import BuilderActions from "@components/builderActions/BuilderActions";
import BuilderActionsTabs from "@components/builderActions/BuilderActionsTabs";
import BuilderMainTabs from "@components/main/BuilderMainTabs";

import { motion } from "framer-motion";
import { Outlet, useLocation } from "react-router-dom";
import { useAppNavigate, useAppSelector, usePermissions, useAppDispatch } from "@app/hooks";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import { Actions } from "@typedefs/interfaces";
import { useEffect, useState, useRef } from "react";
import { useHandleProjectState } from "./useHandleProjectState";
import { encodeProjectId, isEncodedProjectId } from "@utils/projectIdEncoder";
import { useProjectAreaStatus } from "./useProjectAreaStatus";
import { useSynchronizeMapData } from "./useSynchronizeMapData";
import { useProjectCopyBlock } from "@app/hooks/useProjectCopyBlock";
import ProjectCopyBlockModal from "@components/projectCopyBlockModal";
import { apiSlice } from "@app/apiSlice";

/** Indices into `pathname.split("/").filter(Boolean)` for `/project/:id/...` vs `/public/project/:id/...`. */
function projectPathSegmentIndices(isPublic: boolean) {
	const projectSeg = isPublic ? 1 : 0;
	return { projectSeg, projectIdSeg: projectSeg + 1, subpageSeg: projectSeg + 2 };
}

const ProjectContainer = () => {
	const navigate = useAppNavigate();
	const location = useLocation();

	const { project, projectId, isLoading } = useHandleProjectState();
	useProjectAreaStatus();
	useSynchronizeMapData();
	const { isPublic } = usePermissions();
	const [isInResults, setIsInResults] = useState(false);

	const {
		activeAction: activeTabId,
		tabsOpen,
		projectList,
	} = useAppSelector(selectCurrentBuilder);

	const projectIdNum = projectId ? parseInt(projectId, 10) : undefined;
	const { isBlocked, job, progress, status } = useProjectCopyBlock(projectIdNum);
	const prevJobStatusRef = useRef(status);
	const dispatch = useAppDispatch();

	useEffect(() => {
		if (prevJobStatusRef.current === ('pending' as const) && status === 'completed') {
			dispatch(
				apiSlice.util.invalidateTags([
					{ type: 'DetailedProjects', id: projectIdNum },
					{ type: 'FastProjects', id: projectIdNum }
				])
			);
		}
		prevJobStatusRef.current = status;
	}, [status, dispatch, projectIdNum]);

	const handleReturnToList = () => {
		navigate({ path: '/select-project' });
	};

	const actionsGridClass = !tabsOpen
		? "actions-close"
		: [
			Actions.Comments,
			Actions.Help,
			Actions.Changes,
			Actions.Definitions,
		].includes(activeTabId)
			? "actions-small"
			: "";

	/**
	 * Keeps project URLs and UI state aligned with the route.
	 *
	 * **Redirects (replace)** when the path is a project root with no subpage
	 * (`/project/:id` or `/public/project/:id` with nothing after `:id`):
	 * - `new` → private: `/project/new/description`; public: `/auth/login`.
	 * - Numeric or encoded project id → `/project/.../activities` (public paths
	 *   may encode numeric ids); private only redirects for plain digit ids.
	 * - Other segments (e.g. `results` from `/project/results`) are not treated
	 *   as ids, so we do not redirect.
	 *
	 * **State:** sets `isInResults` when the subpage segment is `results`, so
	 * the layout can show results tabs and adjust main chrome.
	 */
	useEffect(() => {
		const segments = location.pathname.split("/").filter(Boolean);
		const { projectSeg, projectIdSeg, subpageSeg } = projectPathSegmentIndices(isPublic);

		const onProjectRoute = segments[projectSeg] === "project";
		const subpageSegment = segments[subpageSeg];
		const missingSubpage = subpageSegment === undefined || subpageSegment === "";

		if (onProjectRoute && missingSubpage) {
			const segmentId = segments[projectIdSeg];

			if (segmentId === "new") {
				const redirectPath = isPublic ? "/auth/login" : "/project/new/description";
				navigate({ path: redirectPath, options: { replace: true }, ignorePublic: true });
			} else if (segmentId) {
				// Only redirect when :projectId looks like a real id (e.g. not "results" from a bad /project/results URL).
				if (isPublic) {
					const numericId = Number(segmentId);
					if (!isNaN(numericId) && numericId > 0 && String(numericId) === segmentId) {
						const encodedId = encodeProjectId(numericId);
						navigate({ path: `/project/${encodedId}/activities`, options: { replace: true } });
					} else if (isEncodedProjectId(segmentId)) {
						navigate({ path: `/project/${segmentId}/activities`, options: { replace: true } });
					}
				} else if (/^\d+$/.test(segmentId)) {
					navigate({ path: `/project/${segmentId}/activities`, options: { replace: true }, ignorePublic: true });
				}
			}
		}

		setIsInResults(onProjectRoute && subpageSegment === "results");
	}, [location.pathname, navigate, isPublic]);

	return (
		<motion.div className={`builder-container ${actionsGridClass}`}>
			<Sidebar />
			<BuilderTopbar
				projectLabel={project ? project.name : "New Project"}
				isLoading={isLoading}
				stepsList={projectList}
			/>
			<BuilderMain className={isInResults ? "results-view" : "bt-1-common-dark"}>
				{!isBlocked && <Outlet />}
			</BuilderMain>
			{
				isInResults ?
					<BuilderMainTabs isInResults />
					: null
			}
			<BuilderActionsTabs />
			<BuilderActions />
			<ProjectCopyBlockModal
				isOpen={isBlocked}
				progress={progress}
				status={status}
				errorMessage={job?.error_message}
				onReturnToList={handleReturnToList}
			/>
		</motion.div>
	);
};

export default ProjectContainer;
